import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AnalyticsProvider } from 'use-analytics';
import { Route, Router } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ruRU } from '@mui/x-date-pickers/locales';
import { QueryParamProvider } from 'use-query-params';
import { SnackbarProvider } from 'notistack';
import { createBrowserHistory } from 'history';
import { observer } from 'mobx-react-lite';
import { ErrorBoundary } from './components';
import { errorStore, routerStore, syncHistoryWithStore } from './stores';
import { analytics } from './utils/analytics';
import { InversifyProvider, LocaleProvider } from './context';
import { getInitialLocale } from './utils/i18n';
import { getDateFnsLocale } from './utils/date';
import Routes from './routes';
import theme from './theme';
import styles from './App.module.css';
import { container } from './config/ioc';

const browserHistory = createBrowserHistory({
  basename: ``,
});
const history = syncHistoryWithStore(browserHistory, routerStore);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App = observer(() => {
  const locale = getInitialLocale();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={1}
        classes={{ containerRoot: styles.snackbarContainer }}
      >
        <QueryClientProvider client={queryClient}>
          <AnalyticsProvider instance={analytics}>
            <Router history={history}>
              <QueryParamProvider ReactRouterRoute={Route}>
                <InversifyProvider container={container}>
                  <LocaleProvider lang={locale}>
                    <ErrorBoundary errors={errorStore.errors}>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={getDateFnsLocale(locale)}
                        localeText={
                          ruRU.components.MuiLocalizationProvider.defaultProps
                            .localeText
                        }
                      >
                        <Routes />
                      </LocalizationProvider>
                    </ErrorBoundary>
                  </LocaleProvider>
                </InversifyProvider>
              </QueryParamProvider>
            </Router>
          </AnalyticsProvider>
        </QueryClientProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
});

export default App;
